import { IAstOptions } from '../index.d';
import { matchFqnPath, mathModelField } from '../../utils/fqn';
import { TokenValidateError } from '../errors';
import { DefaultPromptMessages } from '../../editor/config';
import {
  IToken, IPlaceholderToken, ICustomPropertyToken,
  IDeclarationToken, IFqnToken, IFunctionToken, IModelFieldToken
} from '../typings/token';
import { transformFunctionsToObject } from '../../helpers/transforms';


export function validatePatchTokens(tokens: IToken[], options: IAstOptions) {
  for (const token of tokens) {
    if (token.isPlaceholder) {
      const { placeholder: { validate } } = token as IPlaceholderToken;
      if (validate) {
        const validateResult = validate();
        if (validateResult !== true) {
          throw tokenValidateError(token, validateResult);
        }
      }
    }

    if (token.type === 'FqnProperty') {
      const path = token.raw;
      const { presetPaths = {} } = options;

      if (presetPaths[path]) {
        (token as IFqnToken).fqn = presetPaths[path];
        continue;
      }

      const fqn = matchFqnPath(options.fqn!, path);

      if (fqn) {
        fqn.path = path;
        (token as IFqnToken).fqn = fqn;
      } else {
        throw tokenValidateError(token, `无效的数据模型路径 \`${path}\``);
      }
    }

    else if (token.type === 'ModelProperty') {
      const { models = [], presetPaths = {} } = options;
      const { path, model, raw } = token as IModelFieldToken;

      if (presetPaths[raw]) {
        (token as IModelFieldToken).field = presetPaths[raw];
        continue;
      }

      const modelObj = models.find(item => item.fqn === model);
      if (!modelObj) {
        throw tokenValidateError(token, `无效的数据模型 \`${model}\``);
      }
      const field = mathModelField(modelObj, path);
      if (!field) {
        throw tokenValidateError(token, `无效的数据模型路径 \`${path}\``);
      }
      field.path = path;
      (token as IModelFieldToken).field = field;
    }

    else if (token.type === 'CustomProperty') {
      const { value } = token as ICustomPropertyToken;
      const { customProperties = [], promptMessages = DefaultPromptMessages } = options;
      const customProperty = customProperties.find(item => item.id === value);
      if (!customProperty) {
        throw tokenValidateError(token, promptMessages.invalidCustomProperty);
      } else if (customProperty.disabled) {
        throw tokenValidateError(token, promptMessages.disabledCustomPropertyTooltip);
      } else {
        (token as ICustomPropertyToken).property = customProperty;
      }
    } else if (token.type === 'Function') {
      const functions = transformFunctionsToObject(options.functions);
      const functionName = (token as IFunctionToken).code;
      if (!functions.find(item => item.code.toUpperCase() === functionName)) {
        throw new TokenValidateError('invalid-function', `无效的函数 \`${token.raw}\``, token.position);
      }
    } else if ((token as IDeclarationToken).isDeclaration) {
      const { declaration, value } = token as IDeclarationToken;
      if (declaration === 'CURRENT_MEMBER_GRADE') {
        const { gradeHierarchies = [], promptMessages = {} } = options;
        const gradeHierarchy = gradeHierarchies.find(item => item.id === Number(value));
        if (!gradeHierarchy) {
          throw tokenValidateError(token, promptMessages.invalidGradeHierarchy);
        } else if (gradeHierarchy.disabled) {
          throw tokenValidateError(token, promptMessages.disabledGradeHierarchyTooltip);
        }
      } else if (declaration === 'CURRENT_MEMBER_POINT') {
        const { pointAccounts = [], promptMessages = {} } = options;
        const pointAccount = pointAccounts.find(item => item.id === Number(value));
        if (!pointAccount) {
          throw tokenValidateError(token, promptMessages.invalidPointAccount);
        } else if (pointAccount.disabled) {
          throw tokenValidateError(token, promptMessages.disabledPointAccountTooltip);
        }
      }
    }
  }
}


function tokenValidateError(token: IToken, message: string = '') {
  return new TokenValidateError('invalid-token', message, token.position);
}
